import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API_ENDPOINTS } from '../config/api-config';
import axios from '../utils/axios-config';
import { useAuthStore } from '../stores';

/**
 * Authentication hook for managing user sessions
 * Uses Zustand for state management + TanStack Query for API calls
 * Integrates with wallet-based authentication via thirdweb
 */
export function useAuth() {
  const queryClient = useQueryClient();

  // Get auth state from Zustand store
  const {
    user,
    isAuthenticated,
    isLoading,
    walletAddress,
    isAutoConnecting,
    walletTimeout,
    setUser,
    login: loginStore,
    logout: logoutStore,
    updateUser,
    updateWalletAddress,
    setWalletTimeout,
  } = useAuthStore();

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async ({ email, password }) => {
      const res = await axios.post(API_ENDPOINTS.AUTH.LOGIN, {
        email,
        password,
      });
      return res.data.user;
    },
    onSuccess: (userData) => {
      loginStore(userData);
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: async ({ name, email, password }) => {
      const res = await axios.post(API_ENDPOINTS.AUTH.REGISTER, {
        name,
        email,
        password,
      });
      return res.data.user;
    },
    onSuccess: (userData) => {
      loginStore(userData);
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      // Call logout API if it exists
      try {
        await axios.post(
          API_ENDPOINTS.AUTH.LOGOUT || '/omniverse/auth/logout'
        );
      } catch (error) {
        console.log('Logout API not available or failed', error);
      }
    },
    onSettled: () => {
      logoutStore();
      queryClient.clear(); // Clear all queries
    },
  });

  // Refetch user data
  const refetchUser = async () => {
    if (!user?.id && !user?.oid && !user?.wallet) return null;

    try {
      const userId = user.id || user.oid || user.wallet;
      const res = await axios.get(API_ENDPOINTS.USERS.GET_BY_ID(userId));
      if (res.data?.user) {
        updateUser(res.data.user);
      }
      return res.data;
    } catch (error) {
      console.error('Failed to refetch user:', error);
      return null;
    }
  };

  return {
    // State
    user,
    isAuthenticated,
    isLoading,
    walletAddress,
    isAutoConnecting,
    walletTimeout,
    isWaitingForWallet: isAutoConnecting || (!walletAddress && !walletTimeout),

    // Actions
    login: loginMutation.mutateAsync,
    register: registerMutation.mutateAsync,
    logout: () => logoutMutation.mutate(),
    updateUser,
    setUser,
    updateWalletAddress,
    setWalletTimeout,
    refetchUser,

    // Mutation states
    isLoggingIn: loginMutation.isPending,
    isRegistering: registerMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    loginError: loginMutation.error,
    registerError: registerMutation.error,
  };
}
